import * as React from 'react';
import styled from 'styled-components';
import { useGetProjectImportsQuery } from '../redux/rtkQuery/apiSlice';
import { leadAddress, leadFeesTotal, ProjectImportLead } from '../utils/projectImportFormValues';

const POLL_MS = 60000;

type Props = {
  onReview: (lead: ProjectImportLead) => Promise<void>;
};

const formatSignedAt = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

/**
 * Dashboard notice listing won Monday deals (EPIC project-import drafts) that
 * still need the office to create the project. One row per lead; a lead drops
 * off once its project is saved through the wizard.
 */
const MondayLeadsNotice = ({ onReview }: Props) => {
  const { data } = useGetProjectImportsQuery(undefined, { pollingInterval: POLL_MS });
  const [openingId, setOpeningId] = React.useState<number | null>(null);
  const [error, setError] = React.useState('');

  // Paginated ({count, results}) under the default DRF settings.
  const leads: ProjectImportLead[] = Array.isArray(data) ? data : data?.results || [];
  const count: number = data?.count ?? leads.length;
  if (!leads.length) return null;

  const handleReview = async (lead: ProjectImportLead) => {
    setError('');
    setOpeningId(lead.id);
    try {
      await onReview(lead);
    } catch {
      setError(`Could not open lead ${lead.projectNumber}. Please try again.`);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <Card role="region" aria-label="New leads from Monday">
      <Header>
        <Bell aria-hidden>🔔</Bell>
        <Title>New leads from Monday</Title>
        <CountPill>{count}</CountPill>
        <Hint>Review each lead in the Add Project wizard and save it to create the project.</Hint>
      </Header>
      {error && <ErrorText>{error}</ErrorText>}
      <List>
        {leads.map(lead => {
          const total = leadFeesTotal(lead);
          const signed = formatSignedAt(lead.signedAt);
          return (
            <Row key={lead.id}>
              <Info>
                <LeadName>{lead.projectName}</LeadName>
                <Meta>{leadAddress(lead)}</Meta>
                <Meta>
                  {[
                    lead.projectNumber,
                    lead.requester?.name && `Requester: ${lead.requester.name}`,
                    lead.permits?.length && `${lead.permits.length} permit${lead.permits.length > 1 ? 's' : ''}`,
                    total && `$${total.toFixed(2)}`,
                    signed && `Signed ${signed}`,
                  ].filter(Boolean).join(' · ')}
                </Meta>
                <Meta>
                  Assigned to: {lead.assignedEmployeeFullName || `${lead.assignedEmployeeName || 'Unassigned'} (whole office)`}
                </Meta>
              </Info>
              <ReviewButton
                type="button"
                onClick={() => handleReview(lead)}
                disabled={openingId !== null}
              >
                {openingId === lead.id ? 'Opening…' : 'Review & create'}
              </ReviewButton>
            </Row>
          );
        })}
      </List>
    </Card>
  );
};

export default MondayLeadsNotice;

const Card = styled.section`
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue',
    Arial, Roboto, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  background: var(--neutral-0);
  border: 1px solid var(--primary-100);
  border-left: 4px solid var(--primary-600);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 20px 24px;
  margin-bottom: 20px;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  margin-bottom: 14px;
`;

const Bell = styled.span`
  font-size: 1.125rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
`;

const CountPill = styled.span`
  min-width: 22px;
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px solid var(--primary-100);
  background: var(--primary-50);
  color: var(--primary-700);
  font-size: 0.75rem;
  font-weight: 700;
  text-align: center;
  box-sizing: border-box;
`;

const Hint = styled.span`
  flex-basis: 100%;
  font-size: 0.8125rem;
  color: var(--neutral-500);
`;

const ErrorText = styled.div`
  margin-bottom: 10px;
  font-size: 0.75rem;
  color: var(--danger-500);
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 40vh;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-50);
  box-sizing: border-box;

  & + & {
    margin-top: 8px;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const LeadName = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--neutral-800);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Meta = styled.div`
  margin-top: 2px;
  font-size: 0.8125rem;
  color: var(--neutral-600);
  overflow-wrap: anywhere;
`;

const ReviewButton = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;
